import {
  Anchor,
  Badge,
  Box,
  Center,
  createStyles,
  Group,
  RingProgress,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import React, {useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import {
  NotePerformanceSummary,
  TableLimitUtils,
  TOTAL_FORMULA_COST_LIMIT,
} from 'notebook/performance/tableLimitUtils';
import {TablePerformanceSummaryTable} from 'notebook/performance/TablePerformanceSummaryTable';
import {Table} from 'notebook/tables/constants/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {getEntryOrFileIdKey} from 'notebook/util/analytics/FileAnalyticsUtils';
import {AnyStructuredTable, Note} from 'notebook/util/NoteUtilConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {Title} from 'ui-library/Headers';
import useOnMount from 'ui-library/hooks/useOnMount';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';
import {capitalize} from 'util/NameUtil';

import {computeModalInfo} from './performanceMessagesUtil';

interface NotebookPerformanceMonitorModalProps {
  note: Note;
  fileId: string;
  onRequestClose: () => void;
}

interface MultiDocumentPerformanceMonitorModalProps {
  documentIds: Array<string>;
  worksheetId: string;
  onRequestClose: () => void;
}

// This is its own object to make it possible to stub in tests with sinon
function trackPerformanceMonitorOpened(
  performanceSummary: NotePerformanceSummary,
  type: string,
  idProperties: Record<string, string>
): void {
  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_NOTEBOOK_PERFORMANCE_MONITOR, {
    type,
    severity: TableLimitUtils.getFormulaCostTier(performanceSummary.totalFormulaCost),
    total_formula_cost: performanceSummary.totalFormulaCost,
    total_cell_count: performanceSummary.numCells,
    total_table_count: performanceSummary.numTables,
    total_document_count: performanceSummary.numDocuments,
    ...idProperties,
  });
}

export function MultiDocumentPerformanceMonitorModal({
  onRequestClose,
  documentIds,
  worksheetId,
}: MultiDocumentPerformanceMonitorModalProps): JSX.Element {
  const performanceSummary = useMemo(() => {
    const tablesByDocumentName: {[documentName: string]: Array<Table | AnyStructuredTable>} = {};
    for (const documentId of documentIds) {
      const document = DocumentStore.getDocument(documentId);
      if (document) {
        tablesByDocumentName[document.name] = TableUtil.getTablesFromNote(
          document.documentVersion.entryVersion.note,
          {
            addRunSchemaName: true,
            removeMixtureIngredientSuffix: true,
          }
        );
      }
    }
    return TableLimitUtils.analyzePerformanceOfTables(tablesByDocumentName);
  }, [documentIds]);

  useOnMount(() => {
    trackPerformanceMonitorOpened(performanceSummary, ModelTypeConstants.WORKSHEET, {
      worksheet_id: worksheetId,
    });
  });

  return (
    <div>
      <PerformanceModal
        title="Worksheet Table Performance Breakdown"
        noun="worksheet"
        onRequestClose={onRequestClose}
        performanceSummary={performanceSummary}
      />
    </div>
  );
}

export function NotebookPerformanceMonitorModal({
  onRequestClose,
  note,
  fileId,
}: NotebookPerformanceMonitorModalProps): JSX.Element {
  const performanceSummary = useMemo(() => {
    const tables = TableUtil.getTablesFromNote(note, {
      addRunSchemaName: true,
      removeMixtureIngredientSuffix: true,
    });
    return TableLimitUtils.analyzePerformanceOfTables({entry: tables});
  }, [note]);

  useOnMount(() => {
    trackPerformanceMonitorOpened(performanceSummary, APIUtil.apiIdToType(fileId), {
      [getEntryOrFileIdKey(fileId)]: fileId,
    });
  });

  return (
    <div
      onMouseDown={(e): void => {
        e.stopPropagation();
      }}
    >
      <PerformanceModal
        title="Table Performance Breakdown"
        noun="entry"
        onRequestClose={onRequestClose}
        performanceSummary={performanceSummary}
      />
    </div>
  );
}

function PerformanceModal({
  title,
  noun,
  onRequestClose,
  performanceSummary,
}: {
  title: string;
  noun: string;
  onRequestClose: () => void;
  performanceSummary: NotePerformanceSummary;
}): JSX.Element {
  return (
    <Modal
      title={title}
      onRequestClose={onRequestClose}
      footer={<Footer />}
      buttons={[{text: 'Done', cancel: true, style: ButtonStyle.SECONDARY}]}
    >
      <NotebookPerformanceMonitor noun={noun} performanceSummary={performanceSummary} />
    </Modal>
  );
}

export function NotebookPerformanceMonitor({
  noun,
  performanceSummary,
}: {
  noun: string;
  performanceSummary: NotePerformanceSummary;
}): JSX.Element {
  const theme = useMantineTheme();
  const {classes} = useStyles();
  const {numCells, totalFormulaCost, tableSummaries, numDocuments} = performanceSummary;
  const hasMultipleDocuments = numDocuments > 1;
  const ringPct = totalFormulaCost / TOTAL_FORMULA_COST_LIMIT;

  const {ringColor, ringIcon, badgeColor, badgeText, message, additionalMessage, tables} = useMemo(
    () => computeModalInfo(totalFormulaCost, tableSummaries, theme, noun),
    [totalFormulaCost, tableSummaries, theme, noun]
  );

  return (
    <Box>
      <Group>
        <RingProgress
          sections={[{value: Math.min(ringPct, 1) * 100, color: ringColor}]}
          label={
            <Center>
              <BenchlingIcon icon={ringIcon} className={classes.ringIcon} color={ringColor} />
            </Center>
          }
        />
        <Stack spacing="xs" className={classes.flexItem}>
          <Group>
            <Title>{`${capitalize(noun)} performance`}</Title>
            <Badge color={badgeColor} size="md">
              <Text fw={700}>{badgeText}</Text>
            </Badge>
          </Group>
          <Text>{message}</Text>
          {additionalMessage ? <Text>{additionalMessage}</Text> : null}
          <Text color={theme.colors.gray[5]}>{`Total cell count: ${numCells.toLocaleString()}`}</Text>
        </Stack>
      </Group>
      <TablePerformanceSummaryTable
        tableSummaries={tableSummaries}
        tables={tables}
        showStepNameColumn={hasMultipleDocuments}
      />
    </Box>
  );
}

function Footer(): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  return (
    <Box>
      <Anchor
        className={classes.helpLink}
        href="https://help.benchling.com/hc/en-us/articles/35379317900557-Understanding-and-Managing-Notebook-Entry-Size"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Text>Learn about entry performance</Text>
      </Anchor>
    </Box>
  );
}
const useStyles = createStyles(() => ({
  metricsContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
    padding: '18px 0',
  },
  metricValue: {
    fontSize: '1.5em',
    paddingRight: '24px',
  },
  helpLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  ringIcon: {
    fontSize: '24px',
  },
  flexItem: {
    flex: 1,
  },
}));

export const _forTesting = {
  trackPerformanceMonitorOpened,
};
